import { Edge } from '@xyflow/react';

import { IConditionFlow, SceneType } from '@/interface';

import { EDGE_WIDTH, NodeEnums } from '../const/node';
import { templateEdgesScene } from '../edges/templateEdges';
import { ConditionType } from '../nodes/condition/const';
import { templateNode } from '../nodes/templateNode';
import { AppNode } from '../types';
import { getInsertArray } from './getInsertArray';
import { getLastNode } from './getLastNode';
import { getLatestNode } from './getLatestNode';
import { getNodeId } from './getNodeId';
import { getTemplateGroup } from './getTemplateGroup';

/**
 * 根据数据类型自动生成节点和边
 * 循环使用for 代替map 数据量大使用for
 * @param nodes
 * @returns
 */
export const autoAddNodeEdges = (nodes: AppNode[]) => {
  let resultNodes: AppNode[] = [];
  const resultEdges: Edge[] = [];
  const latestSceneNode = getLatestNode(nodes, NodeEnums.scene);
  const lastNode = getLastNode(nodes);

  // 以scene的开始和结束为一组
  if (lastNode?.type === NodeEnums.scene && lastNode?.data?.info?.id) {
    const newNodes = generateNewNodes(lastNode, latestSceneNode);
    resultNodes = [...resultNodes, ...newNodes];

    const edges = generateEdges(newNodes, lastNode);
    resultEdges.push(...edges);
  }

  if (latestSceneNode && latestSceneNode?.data?.info?.id) {
    const id = getNodeId(nodes.length);
    resultEdges.push(templateEdgesScene(id));
    resultNodes.push({
      ...templateNode[NodeEnums.scene],
      id,
    });
  }

  return { addNodes: resultNodes, addEdges: resultEdges };
};

// 生成新节点
export const generateNewNodes = (lastNode: AppNode | undefined, latestSceneNode: AppNode | undefined) => {
  const newNodes: AppNode[] = []; // 初始化新节点数组

  // 获取模版 group
  const group = getTemplateGroup(lastNode?.data?.info); // 根据上一个节点的信息获取模板组
  const isFinal = lastNode?.data?.info?.isFinal; // 判断lastNode是否为结局幕节点
  // 对互动节点进行判断，设置isEmptyNode 属性，用于布局计算
  const isEmptyNode = lastNode?.data?.info?.sceneType === SceneType.SCENE_TYPE_OPTION;

  for (let i = 0; i < group?.length; i++) {
    const node = group[i];
    const id = getNodeId(i); // 生成当前节点的唯一ID

    if (node.type === NodeEnums.addCondition) { // 如果当前节点类型为条件节点
      const newCondition: IConditionFlow = {
        id: '',
        conditionNodeId: '',
        sceneId: latestSceneNode?.data?.info?.id,
        rootId: id, // 设置根节点ID为当前节点ID
        type: ConditionType.PASS,
        isEmpty: true,
      };
      const { addNodes, newRootNode } = getConditionGroup( // 获取条件组，包括新增的节点和新的根节点
        newCondition,
        {
          ...node,
          id,
          data: {
            ...node.data,
            isEmptyNode, // 将当前节点是否为空的信息传递给新节点
            info: {
              ...(node.data as any)?.info,
              id,
              sceneId: latestSceneNode?.data?.info?.id,
              sceneType: latestSceneNode?.data?.info?.sceneType,
            },
          },
        },
        isFinal,
      );

      if (!isFinal) newNodes.push({ ...newRootNode }); // 如果不是最终节点，则将新的根节点添加到新节点数组中

      newNodes.push(...addNodes); // 将新增的节点添加到新节点数组中
    } else { // 如果当前节点类型不是添加条件
      newNodes.push({ ...node, id }); // 直接将当前节点及其ID添加到新节点数组中
    }
  }
  return newNodes; // 返回新生成的节点数组
};

// 生成新边
export const generateEdges = (newNodes: AppNode[], lastNode: AppNode | undefined) => {
  const edges: Edge[] = [];
  let source: string | undefined = undefined;

  for (let i = 0; i < newNodes.length; i++) {
    const node = newNodes[i];
    const target = node.id;
    if (i === 0) {
      source = lastNode?.id;
    } else {
      source = newNodes[i - 1]?.id;
    }
    if (source) {
      const id = `e${source}-${target}`;
      edges.push({ id, source, target, type: 'smoothstep', deletable: false, interactionWidth: EDGE_WIDTH });
    }
  }
  return edges;
};

export const addConditionNode = (nodes: AppNode[], edges: Edge[], newCondition: IConditionFlow) => {
  const index = nodes.findIndex(node => node?.id === newCondition.rootId);
  if (index === -1) return { newNodes: nodes, newEdges: edges };
  const rootNode = nodes[index];
  const { addNodes, addEdges, newRootNode } = getConditionGroup(newCondition, rootNode);
  const updatedNodes = nodes.map((node, idx) => (idx === index ? newRootNode : node));

  let insertIndex = index + 1;
  for (let i = index; i < nodes.length; i++) {
    if (nodes[i].type === NodeEnums.scene) {
      insertIndex = i;
      break;
    }
  }
  const resultNodes = getInsertArray(updatedNodes, insertIndex, addNodes);
  const resultEdges = edges.concat(addEdges);

  return {
    newNodes: resultNodes,
    newEdges: resultEdges,
  };
};

export const getConditionGroup = (
  newCondition: IConditionFlow,
  rootNode: AppNode,
  isFinal?: boolean,
) => {
  const addNodes: AppNode[] = [];
  const addEdges: Edge[] = [];
  const group = isFinal
    ? [NodeEnums.jump]
    : [NodeEnums.passCondition, NodeEnums.successMission, NodeEnums.jump];
  let preNodeId = newCondition.rootId;
  const conditionNodeId = getNodeId(0);
  for (const e of group) {
    const newId = [NodeEnums.passCondition, NodeEnums.trapCondition].includes(e)
      ? conditionNodeId : getNodeId(addNodes.length);

    let newInfo;
    switch (e) {
      case NodeEnums.successMission:
        newInfo = { id: newCondition.triggerMissionId };
        break;
      case NodeEnums.jump:
        newInfo = { nextSceneId: newCondition.nextSceneId };
        break;
      default:
        newInfo = { ...newCondition, conditionNodeId };
        break;
    }

    addNodes.push({
      ...templateNode[e],
      id: newId,
      data: {
        conditionNodeId,
        info: newInfo,
      },
    });

    addEdges.push({
      id: `e${preNodeId}-${newId}`,
      source: preNodeId,
      target: newId,
      type: 'smoothstep',
      deletable: false,
      interactionWidth: EDGE_WIDTH,
    });
    preNodeId = newId;
  }

  // 更新addCondition的info
  const newConditions = addNodes
    .filter(node => node.type === NodeEnums.passCondition
      || node.type === NodeEnums.trapCondition)
    .map(node => node.data.info);
  const newRootNode = {
    ...rootNode,
    data: {
      ...rootNode.data,
      info: {
        ...rootNode.data.info,
        id: rootNode.id,
        conditions: (rootNode.data.info?.conditions || []).concat(newConditions),
      },
    },
  };

  return { addNodes, addEdges, newRootNode };
};
